import './MultipleEditDrawerContentSecondStep.scss';

import { useFormikContext } from 'formik';
import { useMemo } from 'react';

import { validateTagList } from '../../../../../../../common/AdForm/validation/tags';
import Button from '../../../../../../../common/Button';
import { useFieldArray } from '../../../../../../../common/Form/hooks';
import SideNav from '../../../../../../../common/SideNav';
import { showFailure } from '../../../../../../../common/utils';
import type { AdTag } from '../../../../../../../models';
import bem from '../../../../../../../utils/bem';
import { EntityTypeArray } from '../../../../../TraffickingPage/hooks/makePageDrawerPlugin';
import type {
  MultipleEditDrawerAdsFormValues,
  MultipleEditDrawerFormValues,
  MultipleEditDrawerLineItemsFormValues,
} from '../../../types';
import MultipleEditDrawerContentCard from '../../MultipleEditDrawerContentCard';
import MultipleEditDrawerContentHeader from '../../MultipleEditDrawerContentHeader';
import { adsCardsContent, lineItemsCardsContent } from './cardsContent';
import { BufferProvider, useBufferContext } from './cardsContent/Buffer/BufferContext';
import { scrollContainerId } from './constants';
import { getEntitiesWithOriginalProps, getIsSaveButtonDisabled } from './utils';

const [block, element] = bem('multiple-edit-drawer-content-second-step');

export type MultipleEditDrawerContentSecondStepProps = {
  isExpanded: boolean;
  entityType: EntityTypeArray;
  onNextStep: () => void;
};

const EDIT_SECTION_MESSAGE =
  "Check the box to make changes in this section. If the section is not checked, edits won't be applied.";

function MultipleEditDrawerContentSecondStepInner({
  isExpanded,
  entityType,
  onNextStep,
}: Readonly<MultipleEditDrawerContentSecondStepProps>): JSX.Element {
  const { resetForm, values, setFieldValue } = useFormikContext<MultipleEditDrawerFormValues>();
  const { applyAllChanges } = useBufferContext();
  const isLineItem = entityType === EntityTypeArray.LINE_ITEM_ARRAY;

  const [, { value: adTagList }] = useFieldArray<AdTag>('adTagList');

  const tagsError = useMemo(() => validateTagList(adTagList ?? []), [adTagList]);

  const cardsContent = isLineItem ? lineItemsCardsContent : adsCardsContent();
  const sideNavLinks = cardsContent.map(({ sideNavLink }) => sideNavLink);

  const onDiscardChanges = (): void => {
    resetForm();

    const entitiesWithOriginalIsSelectedAndStatus = isLineItem
      ? getEntitiesWithOriginalProps({
          entities: (values as MultipleEditDrawerLineItemsFormValues).entities,
          initialStatuses: values.initialStatuses,
        })
      : getEntitiesWithOriginalProps({
          entities: (values as MultipleEditDrawerAdsFormValues).entities,
          initialStatuses: values.initialStatuses,
        });

    setFieldValue('entities', entitiesWithOriginalIsSelectedAndStatus);
    setFieldValue('initialStatuses', values.initialStatuses);
  };

  const onSave = (): void => {
    if (tagsError) {
      const { message, description } = tagsError;

      showFailure({ message, description });
    } else {
      applyAllChanges();
      onNextStep();
    }
  };

  return (
    <div className={block()}>
      <MultipleEditDrawerContentHeader>
        <div className={element('buttons')}>
          <Button size="medium" onClick={onDiscardChanges} disabled={!values.selectedSections.length}>
            Discard Changes
          </Button>
          <Button size="medium" onClick={onSave} disabled={getIsSaveButtonDisabled({ values, isLineItem })}>
            Save
          </Button>
        </div>
      </MultipleEditDrawerContentHeader>
      <div className={element('edit-message')}>{EDIT_SECTION_MESSAGE}</div>
      <div className={element('body', isLineItem ? 'line-items' : 'ads')}>
        <SideNav links={sideNavLinks} containerId={scrollContainerId} testid="multiple-edit-side-nav" />
        <div className={element('body__content')} id={scrollContainerId}>
          {cardsContent.map(({ sideNavLink, cardContent, title }) => (
            <MultipleEditDrawerContentCard sideNavLink={sideNavLink} key={sideNavLink} title={title}>
              {cardContent(isExpanded, entityType)}
            </MultipleEditDrawerContentCard>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function MultipleEditDrawerContentSecondStep(
  props: Readonly<MultipleEditDrawerContentSecondStepProps>
): JSX.Element {
  return (
    <BufferProvider>
      <MultipleEditDrawerContentSecondStepInner {...props} />
    </BufferProvider>
  );
}
